import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const ServiceCartContext = createContext(null);
const STORAGE_KEY = 'be_service_cart_v1';

export const ServiceCartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) { /* ignore */ }
  }, [items]);

  const addItem = useCallback((service) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.service_id === service.service_id);
      if (existing) {
        return prev.map((i) => i.service_id === service.service_id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, {
        service_id: service.service_id,
        name: service.name,
        price: service.price,
        image: service.image,
        duration_mins: service.duration_mins,
        category_slug: service.category_slug,
        category_name: service.category_name,
        quantity: 1,
      }];
    });
  }, []);

  const updateQty = useCallback((service_id, delta) => {
    setItems((prev) => prev
      .map((i) => i.service_id === service_id ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i)
      .filter((i) => i.quantity > 0));
  }, []);

  const removeItem = useCallback((service_id) => {
    setItems((prev) => prev.filter((i) => i.service_id !== service_id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const total = items.reduce((s, i) => s + (i.price * i.quantity), 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <ServiceCartContext.Provider value={{ items, addItem, updateQty, removeItem, clear, total, count }}>
      {children}
    </ServiceCartContext.Provider>
  );
};

export const useServiceCart = () => {
  const ctx = useContext(ServiceCartContext);
  if (!ctx) throw new Error('useServiceCart must be used within ServiceCartProvider');
  return ctx;
};
